import { createRoute, Outlet, redirect } from "@tanstack/react-router";
import { appLayoutRoute } from "@/routes/layout-routes";
import { SettingsScreen } from "@/app/Settings/Settings/Screens/SettingsScreen";
import SettingsInsuranceScreen from "@/app/Settings/Settings/Screens/SettingsInsuranceScreen";
import { SettingsGeneralScreen } from "@/app/Settings/Settings/Screens/SettingsGeneralScreen";
import { SettingsTeamScreen } from "@/app/Settings/Settings/Screens/SettingsTeamScreen";
import { SettingsGapRentalsScreen } from "@/app/Settings/Settings/Screens/SettingsGapRentalsScreen";
import { SettingsNotificationScreen } from "@/app/Settings/Settings/Screens/SettingsNotificationScreen";
import { SettingsBillingScreen } from "@/app/Settings/Billing/Screens/SettingsBillingScreen";
import { SupplierBillingLayout } from "@/app/Settings/Billing/Screens/SupplierBillingLayout";
import { SettingsSupplierSelectScreen } from "@/app/Settings/Settings/Screens/SettingsSupplierSelectScreen";
import { SettingsVehicleScreen } from "@/app/Settings/Settings/Screens/SettingsVehicleScreen";
import { SettingsOfficeScreen } from "@/app/Settings/Settings/Screens/SettingsOfficeScreen";
import { SettingsReportingScreen } from "@/app/Settings/Settings/Screens/SettingsReportingScreen";
import { SettingsIntegrationScreen } from "@/app/Settings/Settings/Screens/SettingsIntegrationScreen";
import { SettingsIntegrationsScreen } from "@/app/Settings/Settings/Screens/SettingsIntegrationsScreen";
import {
  integrationRecordQuery,
  supplierIntegrationList,
} from "@/app/Settings/Settings/GraphQL/integrationQueries";
import { fetchQuery, prefetchQuery } from "@/lib/GraphQLCodegen/fetcher";
import { integrations } from "@/app/Settings/Integrations/Common/integrations";
import { resolveSettingsSupplierId } from "@/app/Suppliers/Utils/globalSupplier";
import {
  supplierOfficeListQuery,
  supplierOfficeListVariables,
} from "@/app/Offices/GraphQL/supplierOfficeListQuery";
import { supplierTermsAndConditionsRecord } from "@/app/Suppliers/GraphQL/supplierTermsAndConditionsRecord";
import { supplierAcknowledgementList } from "@/app/Suppliers/GraphQL/supplierAcknowledgementList";
import { vehicleListQuery } from "@/app/Vehicles/GraphQL/vehicleListQuery";
import { purchasableExtrasQuery } from "@/app/Suppliers/GraphQL/supplierPurchasableExtrasQuery";
import { supplierInsuranceOptionListQuery } from "@/app/Suppliers/GraphQL/supplierInsuranceOptionListQuery";
import { supplierListQuery } from "@/app/Suppliers/GraphQL/supplierListQuery";
import { organisationSettingsQuery } from "@/app/Settings/Settings/GraphQL/organisationSettingsQuery";
import { organisationInvitationsQuery } from "@/app/Settings/Settings/Components/OrganizationInvitationsList";
import { billingEntitiesQuery } from "@/app/Settings/Settings/GraphQL/billingEntitiesQuery";
import { RelocationType } from "@/gql/graphql";
import { SettingsActiveLinesScreen } from "@/app/Settings/Settings/Screens/SettingsActiveLinesScreen";
import { SettingsActivityScreen } from "@/app/Settings/Settings/Screens/SettingsActivityScreen";

export const settingsRoute = createRoute({
  path: "/settings",
  getParentRoute: () => appLayoutRoute,
  component: SettingsScreen,
  // `supplier` scopes which supplier the settings screens edit when no global
  // supplier is selected, without changing the global selection.
  validateSearch: (search: Record<string, unknown>): { supplier?: string } => {
    const supplier = (search.supplier as string | undefined) || undefined;
    return supplier ? { supplier } : {};
  },
  notFoundComponent: () => null,
  head: () => ({
    meta: [
      {
        title: "Settings - Imoova Admin",
      },
    ],
  }),
});

export const settingsGeneralRoute = createRoute({
  path: "/",
  beforeLoad: ({ params, search }) => {
    const supplierId = resolveSettingsSupplierId(params, search);
    if (!supplierId) {
      throw redirect({
        to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/suppliers",
        params: (prev) => prev,
        search: (prev) => prev,
        from: "/$tenant/$orgId/supplier/{-$supplierId}",
      });
    }
  },
  getParentRoute: () => settingsRoute,
  component: SettingsGeneralScreen,
  // Fire-and-forget: warm the cards' queries so they resolve their own
  // component-level suspense boundaries instead of blanking the page.
  loaderDeps: ({ search }) => ({ supplier: search.supplier }),
  loader: ({ params, deps }) => {
    const supplierId = resolveSettingsSupplierId(params, deps);
    if (!supplierId) {
      return;
    }
    prefetchQuery(
      supplierTermsAndConditionsRecord,
      { supplierId, relocationType: RelocationType.Relocation },
      params.orgId,
    );
    prefetchQuery(
      supplierAcknowledgementList,
      { supplierId, page: 1, first: 100 },
      params.orgId,
    );
  },
  head: () => ({
    meta: [
      {
        title: "General Settings - Imoova Admin",
      },
    ],
  }),
});

export const settingsVehicleRoute = createRoute({
  path: "/vehicles",
  getParentRoute: () => settingsRoute,
  component: SettingsVehicleScreen,
  // Fire-and-forget: warm the vehicle + purchasable-extra grids so they render
  // populated instead of flashing empty on first paint.
  loaderDeps: ({ search }) => ({ supplier: search.supplier }),
  loader: ({ params, deps }) => {
    const supplierId = resolveSettingsSupplierId(params, deps);
    if (!supplierId) {
      return;
    }
    prefetchQuery(
      vehicleListQuery,
      { first: 100, supplier_id: supplierId, archived: false },
      params.orgId,
    );
    prefetchQuery(
      purchasableExtrasQuery,
      { first: 100, supplierId },
      params.orgId,
    );
  },
  head: () => ({
    meta: [
      {
        title: "Vehicle Settings - Imoova Admin",
      },
    ],
  }),
});

export const settingsOfficeRoute = createRoute({
  path: "/offices",
  getParentRoute: () => settingsRoute,
  component: SettingsOfficeScreen,
  // Fire-and-forget: warm the office list (shared by the map/table/trips views)
  // so the card resolves its own suspense boundary instead of blanking the page.
  loaderDeps: ({ search }) => ({ supplier: search.supplier }),
  loader: ({ params, deps }) => {
    const supplierId = resolveSettingsSupplierId(params, deps);
    if (!supplierId) {
      return;
    }
    prefetchQuery(
      supplierOfficeListQuery,
      supplierOfficeListVariables(supplierId),
      params.orgId,
    );
  },
  head: () => ({
    meta: [
      {
        title: "Office Settings - Imoova Admin",
      },
    ],
  }),
});

export const settingsReportRoute = createRoute({
  path: "/reports",
  getParentRoute: () => settingsRoute,
  component: SettingsReportingScreen,
  head: () => ({
    meta: [
      {
        title: "Report Settings - Imoova Admin",
      },
    ],
  }),
});

export const settingsAvailabilityRoute = createRoute({
  path: "/availability",
  beforeLoad: ({ params, search }) => {
    const supplierId = resolveSettingsSupplierId(params, search);
    throw redirect({
      to: supplierId
        ? "/$tenant/$orgId/supplier/{-$supplierId}/settings/offices"
        : "/$tenant/$orgId/supplier/{-$supplierId}/settings/suppliers",
      params: (prev) => prev,
      search: (prev) => prev,
      from: "/$tenant/$orgId/supplier/{-$supplierId}",
    });
  },
  getParentRoute: () => settingsRoute,
});

export const settingsInsuranceRoute = createRoute({
  path: "/insurance",
  beforeLoad: ({ params, search }) => {
    const supplierId = resolveSettingsSupplierId(params, search);
    if (!supplierId) {
      throw redirect({
        to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/suppliers",
        params: (prev) => prev,
        search: (prev) => prev,
        from: "/$tenant/$orgId/supplier/{-$supplierId}",
      });
    }
  },
  getParentRoute: () => settingsRoute,
  component: SettingsInsuranceScreen,
  // Fire-and-forget: warm the insurance-option grid so it renders populated
  // instead of flashing empty on first paint.
  loaderDeps: ({ search }) => ({ supplier: search.supplier }),
  loader: ({ params, deps }) => {
    const supplierId = resolveSettingsSupplierId(params, deps);
    if (!supplierId) {
      return;
    }
    prefetchQuery(
      supplierInsuranceOptionListQuery,
      { first: 100, supplierId },
      params.orgId,
    );
  },
  head: () => ({
    meta: [
      {
        title: "Insurance Settings - Imoova Admin",
      },
    ],
  }),
});

export const settingsGapRentalsRoute = createRoute({
  path: "/gap-rentals",
  beforeLoad: ({ params, search }) => {
    const supplierId = resolveSettingsSupplierId(params, search);
    if (!supplierId) {
      throw redirect({
        to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/suppliers",
        params: (prev) => prev,
        search: (prev) => prev,
        from: "/$tenant/$orgId/supplier/{-$supplierId}",
      });
    }
  },
  getParentRoute: () => settingsRoute,
  component: SettingsGapRentalsScreen,
  // Fire-and-forget: warm the gap-rental terms & conditions card.
  loaderDeps: ({ search }) => ({ supplier: search.supplier }),
  loader: ({ params, deps }) => {
    const supplierId = resolveSettingsSupplierId(params, deps);
    if (!supplierId) {
      return;
    }
    prefetchQuery(
      supplierTermsAndConditionsRecord,
      { supplierId, relocationType: RelocationType.GapRental },
      params.orgId,
    );
  },
  head: () => ({
    meta: [
      {
        title: "Gap Rental Settings - Imoova Admin",
      },
    ],
  }),
});

export const settingNotificationRoute = createRoute({
  path: "/notifications",
  beforeLoad: ({ params, search }) => {
    const supplierId = resolveSettingsSupplierId(params, search);
    if (!supplierId) {
      throw redirect({
        to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/suppliers",
        params: (prev) => prev,
        search: (prev) => prev,
        from: "/$tenant/$orgId/supplier/{-$supplierId}",
      });
    }
  },
  component: SettingsNotificationScreen,
  getParentRoute: () => settingsRoute,
  head: () => ({
    meta: [
      {
        title: "Notification Settings - Imoova Admin",
      },
    ],
  }),
});

export const settingBillingRoute = createRoute({
  path: "/billing",
  beforeLoad: ({ params, search }) => {
    const supplierId = resolveSettingsSupplierId(params, search);
    if (!supplierId) {
      throw redirect({
        to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/suppliers",
        params: (prev) => prev,
        search: (prev) => prev,
        from: "/$tenant/$orgId/supplier/{-$supplierId}",
      });
    }
  },
  component: SupplierBillingLayout,
  getParentRoute: () => settingsRoute,
  // Fire-and-forget: warm the billing entities (drives the layout's default-entity
  // redirect and is reused by the entity screen).
  loaderDeps: ({ search }) => ({ supplier: search.supplier }),
  loader: ({ params, deps }) => {
    const supplierId = resolveSettingsSupplierId(params, deps);
    if (!supplierId) {
      return;
    }
    prefetchQuery(
      billingEntitiesQuery,
      { supplier_id: supplierId },
      params.orgId,
    );
  },
  head: () => ({
    meta: [
      {
        title: "Billing Settings - Imoova Admin",
      },
    ],
  }),
});

export const settingBillingIndexRoute = createRoute({
  path: "/",
  getParentRoute: () => settingBillingRoute,
  component: Outlet,
});

export const settingBillingEntityRoute = createRoute({
  path: "$entityId",
  component: SettingsBillingScreen,
  getParentRoute: () => settingBillingRoute,
  head: () => ({
    meta: [
      {
        title: "Billing Settings - Imoova Admin",
      },
    ],
  }),
});

export const settingIntegrationsRoute = createRoute({
  path: "/integrations",
  component: Outlet,
  getParentRoute: () => settingsRoute,
  head: () => ({
    meta: [
      {
        title: "Integrations - Imoova Admin",
      },
    ],
  }),
});

export const settingIntegrationsListRoute = createRoute({
  path: "/",
  component: SettingsIntegrationsScreen,
  getParentRoute: () => settingIntegrationsRoute,
  // Fire-and-forget: warm the active-integrations list.
  loaderDeps: ({ search }) => ({ supplier: search.supplier }),
  loader: ({ params, deps }) => {
    const supplierId = resolveSettingsSupplierId(params, deps);
    if (!supplierId) {
      return;
    }
    prefetchQuery(
      supplierIntegrationList,
      { supplier_id: supplierId },
      params.orgId,
    );
  },
  head: () => ({
    meta: [
      {
        title: "Integrations - Imoova Admin",
      },
    ],
  }),
});

export const settingsIntegrationRotue = createRoute({
  path: "$integrationId",
  component: SettingsIntegrationScreen,
  loaderDeps: ({ search }) => ({ supplier: search.supplier }),
  loader: async ({ params, deps }) => {
    const res = await fetchQuery(
      integrationRecordQuery,
      { id: params.integrationId },
      params.orgId,
    );

    const supplierId = resolveSettingsSupplierId(params, deps);

    if (res.integration.supplier.id !== supplierId) {
      throw redirect({
        to: "/$tenant/$orgId/supplier/{-$supplierId}/settings/integrations",
        params: (prev) => prev,
        search: (prev) => prev,
        from: "/$tenant/$orgId/supplier/{-$supplierId}",
      });
    }

    return res;
  },
  getParentRoute: () => settingIntegrationsRoute,
  head: ({ loaderData }) => {
    const name = integrations.find(
      (i) => i.type === loaderData?.integration.type,
    )?.name;
    return {
      meta: [
        {
          title: `${name ?? "Integration"} - Imoova Admin`,
        },
      ],
    };
  },
});

export const settingsOrganisationRoute = createRoute({
  path: "/organisation",
  component: SettingsTeamScreen,
  getParentRoute: () => settingsRoute,
  // Fire-and-forget: warm the org settings + invitations (org-scoped, no supplier).
  loader: ({ params }) => {
    prefetchQuery(organisationSettingsQuery, {}, params.orgId);
    prefetchQuery(
      organisationInvitationsQuery,
      { organisation_id: params.orgId, first: 20, page: 1 },
      params.orgId,
    );
  },
  head: () => ({
    meta: [
      {
        title: "Organisation Settings - Imoova Admin",
      },
    ],
  }),
});

export const settingsCitiesRoute = createRoute({
  path: "/cities",
  getParentRoute: () => settingsRoute,
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/$tenant/$orgId/supplier/{-$supplierId}/admin/cities",
      params,
      statusCode: 301,
    });
  },
});

export const settingsCityDrawerRoute = createRoute({
  path: "$cityId",
  getParentRoute: () => settingsCitiesRoute,
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/$tenant/$orgId/supplier/{-$supplierId}/admin/cities/$cityId",
      params,
      statusCode: 301,
    });
  },
});

export const settingsSuppliersRoute = createRoute({
  path: "/suppliers",
  component: SettingsSupplierSelectScreen,
  getParentRoute: () => settingsRoute,
  loader: ({ params }) =>
    fetchQuery(
      supplierListQuery,
      { first: 200, page: 1, archived: false, search: undefined },
      params.orgId,
    ),
  head: () => ({
    meta: [
      {
        title: "Select Supplier - Imoova Admin",
      },
    ],
  }),
});

export const settingsDefaultRatesRoute = createRoute({
  path: "/default-rates",
  getParentRoute: () => settingsRoute,
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/$tenant/$orgId/supplier/{-$supplierId}/admin/default-rates",
      params,
      statusCode: 301,
    });
  },
});

export const settingsAgreementsRoute = createRoute({
  path: "/agreements",
  getParentRoute: () => settingsRoute,
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/$tenant/$orgId/supplier/{-$supplierId}/admin/agreements",
      params,
      statusCode: 301,
    });
  },
});

export const settingsActiveLinesRoute = createRoute({
  path: "/active-lines",
  component: SettingsActiveLinesScreen,
  getParentRoute: () => settingsRoute,
  head: () => ({
    meta: [
      {
        title: "Active Plates - Imoova Admin",
      },
    ],
  }),
});

export const settingsActivityRoute = createRoute({
  path: "/activity",
  component: SettingsActivityScreen,
  getParentRoute: () => settingsRoute,
  head: () => ({
    meta: [
      {
        title: "Activity - Imoova Admin",
      },
    ],
  }),
});

export const settingsToolsRoute = createRoute({
  path: "/tools",
  getParentRoute: () => settingsRoute,
  validateSearch: (search: Record<string, unknown>) => ({
    relocationPageId: search.relocationPageId as string | undefined,
  }),
  beforeLoad: ({ params, search }) => {
    throw redirect({
      to: "/$tenant/$orgId/supplier/{-$supplierId}/admin/relocation-pages",
      params,
      search,
      statusCode: 301,
    });
  },
});
